// External
import React, { useContext, useState, useEffect } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { useParams, Link, useHistory } from 'react-router-dom';
import * as _ from 'lodash';
import { useFieldArray, useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputMask from 'react-input-mask';
import {
  MinusSmIcon,
  PlusSmIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/solid';

// Internal
import {
  ContactDetails_ContactData,
  ContactDetails_ContactData_contact,
  ContactDetails_ContactData_contact_person,
  ContactDetails_ContactData_contact_company,
  ContactDetails_ContactData_contact_person_socialLinks,
  ContactDetails_ContactData_contact_company_socialLinks,
} from '../../types/graph/ContactDetails_ContactData';
import useNotifier from '../../hooks/useNotifier';
import paths from '../../constants/routes.json';
import getModalContext from '../../context/getModalContext';
import ImageUploadField from '../../components/ImageUploadField/ImageUploadField';
import { SaveFormButton, CancelFormButton } from '../../components';
import { DatabaseIcon } from '@heroicons/react/outline';
import { CONTACTS_QUERY } from '../People';
import { UPDATE_LISTING } from '../Listings/Details/Contacts';

// Component interface
interface ContactDetailsParams {
  contactId: string;
  listingId: string;
}

// Form data validation schema
const validationSchema = yup.object().shape({
  person: yup.object().shape({
    name: yup.object().shape({
      full: yup.string().nullable(),
    }),
    email: yup
      .string()
      .email('Valid email is required')
      .required('An email is required.'),
    phone: yup.array().of(
      yup.object().shape({
        number: yup.string(),
        type: yup.string(),
      })
    ),
    socialLinks: yup.array().of(
      yup.object().shape({
        url: yup.string(),
        type: yup.string(),
      })
    ),
  }),
  company: yup.object().shape({
    title: yup.string(),
    name: yup.string(),
    socialLinks: yup.array().of(
      yup.object().shape({
        url: yup.string(),
        type: yup.string(),
      })
    ),
  }),
});

const ContactDetails: React.FC = () => {
  /**
   * States
   */
  const [loading, setLoading] = useState<boolean>(false);
  const [avatarPreviewSrc, setAvatarPreviewSrc] = useState<string | null>(null);
  const [logoPreviewSrc, setLogoPreviewSrc] = useState<string | null>(null);
  const [personWebsiteURL, setPersonWebsiteURL] = useState<string>('');
  const [companyWebsiteURL, setCompanyWebsiteURL] = useState<string>('');
  const [avatarPreview, setAvatarPreview] = useState<string>('');

  /**
   * Hooks
   */

  const { contactId, listingId } = useParams<ContactDetailsParams>();

  let history = useHistory();
  // Context for modal
  const modals = useContext(getModalContext());

  // Notification
  const notifier = useNotifier();

  const [updateListing] = useMutation(UPDATE_LISTING, {
    onCompleted: () => {
      console.log('updateListing: ');
    },
  });

  // Contact data Graph Query
  const {
    data,
    loading: contactLoading,
    error,
  } = useQuery<ContactDetails_ContactData>(GET_CONTACT, {
    variables: { contactId },
    skip: contactId === 'new',
  });

  // Add Contact Graph Mutation
  const [addContact] = useMutation(ADD_CONTACT, {
    onCompleted: (data: any) => {
      setLoading(false);
      notifier.showMessage(
        'Successfully saved!',
        'New Contact details have been saved.',
        'success'
      );

      //TODO: This creates a link problem to Listings.
      // Migrate to event architecture once available.
      if (listingId)
        updateListing({
          variables: {
            listingId: listingId,
            listing: {
              contacts: [data.contactAdd.id],
            },
          },
        });

      history.goBack();
    },
    refetchQueries: [{ query: CONTACTS_QUERY }],
  });

  // Update Contact Graph Mutation
  const [updateContact] = useMutation(UPDATE_CONTACT, {
    onCompleted: () => {
      setLoading(false);
      notifier.showMessage(
        'Successfully updated!',
        'Contact details have been updated.',
        'success'
      );
    },
    refetchQueries: [{ query: CONTACTS_QUERY }],
  });

  if (error) {
    console.error(error);
  }

  const loadCurrentContact = {
    person: {
      socialLinks: data?.contact?.person?.socialLinks
        ? data?.contact!.person!.socialLinks!.map((i: any) => {
            return { type: i.type, url: i.url }; // To extract the __typename
          })
        : [{ type: 'Facebook', url: '' }],
    },
    company: {
      socialLinks: data?.contact?.company?.socialLinks
        ? data?.contact!.company!.socialLinks!.map((i: any) => {
            return { type: i.type, url: i.url }; // To extract the __typename
          })
        : [{ type: 'Facebook', url: '' }],
    },
  };

  // Form Management
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isDirty },
    reset: resetForm,
  } = useForm<ContactDetails_ContactData_contact>({
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: loadCurrentContact,
  });

  // Form SocialLinks Array Management
  const {
    fields: pslFields,
    append: pslAppend,
    remove: pslRemove,
  } = useFieldArray({
    control,
    name: 'person.socialLinks',
    //keyName: 'url',
  });

  const {
    fields: cslFields,
    append: cslAppend,
    remove: cslRemove,
  } = useFieldArray({
    control,
    name: 'company.socialLinks',
    //keyName: 'url',
  });

  const loadWebsiteURL = () => {
    const personalWebsite = _.get(
      data,
      'contact.person.socialLinks',
      []
    ).filter((i: any) => i.type === 'Website');

    if (personalWebsite.length > 0) {
      setPersonWebsiteURL((personalWebsite as any[])[0].url);
    }

    const companyWebsite = _.get(data, 'contact.person.socialLinks', []).filter(
      (i: any) => i.type === 'Website'
    );

    if (companyWebsite.length > 0) {
      setCompanyWebsiteURL((companyWebsite as any[])[0].url);
    }
  };

  useEffect(() => {
    loadWebsiteURL();
    resetForm(loadCurrentContact);
  }, [data]);

  /**
   * Handlers
   */

  // On form submit action
  const onSubmit = (data: DeepPartial<ContactDetails_ContactData_contact>) => {
    if (!data.person)
      data.person = {} as ContactDetails_ContactData_contact_person;
    if (!data.person?.socialLinks) data.person.socialLinks = [];

    // Remove all Website items as well as all the empty items
    _.remove(
      data.person.socialLinks,
      (i: any) => i?.url === '' || i?.type === 'Website'
    );

    if (personWebsiteURL === null || personWebsiteURL === undefined) {
      data.person.socialLinks.push({
        url: '',
        type: 'Website',
      });
    } else
      data.person.socialLinks.push({
        url: personWebsiteURL,
        type: 'Website',
      });

    if (!data.company)
      data.company = {} as ContactDetails_ContactData_contact_company;
    if (!data.company?.socialLinks) data.company.socialLinks = [];

    // Remove all Website items as well as all the empty items
    _.remove(
      data.company.socialLinks,
      (i) => i?.url === '' || i?.type === 'Website'
    );
    if (companyWebsiteURL === null || companyWebsiteURL === undefined) {
      data.company.socialLinks.push({
        url: '',
        type: 'Website',
      });
    } else
      data.company.socialLinks.push({
        url: companyWebsiteURL,
        type: 'Website',
      });

    if (_.get(data, 'person.avatar', []).length <= 0) {
      delete data.person.avatar;
    }
    if (_.get(data, 'company.logo', []).length <= 0) {
      delete data.company.logo;
    }

    if (contactId === 'new') {
      setLoading(true);
      addContact({ variables: { contact: data } });
    } else {
      setLoading(true);
      updateContact({
        variables: { contactId: contactId, contact: data },
      });
    }
  };

  const numberOfPersonSocialLinks = (): number => {
    let counter: number = 0;
    pslFields.map((field: any, index) => {
      if (field.type !== 'Website') counter = counter + 1;
    });
    return counter;
  };

  const numberOfCompanySocialLinks = (): number => {
    let counter: number = 0;
    cslFields.map((field: any, index) => {
      if (field.type !== 'Website') counter = counter + 1;
    });
    return counter;
  };

  const resetFields = () => {
    loadWebsiteURL();
    resetForm();
  };

  if (contactLoading && !listingId) {
    return <div>Loading...</div>;
  }

  return (
    <form className="mx-auto max-w-5xl" onSubmit={handleSubmit(onSubmit)}>
      {/* Contact Details form */}
      {/* Header Action section - top buttons*/}
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {`${contactId === 'new' ? 'New Contact' : 'Contact Details'}`}
        </h3>
        <div className="space-x-3 flex items-center">
          {!listingId ? (
            <Link
              to={paths.PEOPLE}
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
            >
              Back to Contacts
            </Link>
          ) : (
            <Link
              to={`${paths.LISTINGS}/${listingId}/contacts`}
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light"
            >
              Back to Listing
            </Link>
          )}
          {contactId !== 'new' && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              onClick={() => {
                modals?.setModalState('contactDeleteModal', true, {
                  contactId: data?.contact?.id,
                });
              }}
            >
              {/* Heroicon name: outline/trash */}

              <svg
                className="-mx-1 h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="space-y-6">
        {/* Personal Information section */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Personal Information
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This information will be displayed publicly if added as a
                contact card for a listing.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              {/** <form className="space-y-6" action="#" method="POST">*/}
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <InputGroup label="Full Name">
                    <InputField
                      type="text"
                      {...register(`person.name.full`)}
                      defaultValue={_.get(data, 'contact.person.name.full', '')}
                      error={(errors.person as any)?.name?.full}
                    />
                  </InputGroup>
                  <InputGroup label="Email address">
                    <InputField
                      type="text"
                      {...register(`person.email`)}
                      defaultValue={_.get(data, 'contact.person.email', '')}
                      error={(errors.person as any)?.email}
                    />
                  </InputGroup>
                  <InputGroup label="Phone Number">
                    <TextSelectField
                      selectProps={{
                        ...register(`person.phones.0.type` as const),
                        defaultValue: _.get(
                          data,
                          `contact.person.phones[0].type`,
                          ''
                        ),
                      }}
                      selectOptions={['Mobile', 'Work', 'Home']}
                      inputProps={{
                        ...register(`person.phones.0.number` as const),
                        defaultValue: _.get(
                          data,
                          `contact.person.phones[0].number`,
                          ''
                        ),
                        mask: '(999) 999-9999',
                      }}
                      placeholder={'(999) 999-9999'}
                    />
                  </InputGroup>
                </div>
                <div className="space-y-1 col-span-6 sm:col-span-3">
                  <InputGroup label="Personal Website URL">
                    <PrefixInputField
                      type="text"
                      prefix="http://"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.value
                          ? setPersonWebsiteURL(e.target.value)
                          : setPersonWebsiteURL('');
                        pslAppend({
                          url: personWebsiteURL,
                          type: 'Website',
                        });
                      }}
                      defaultValue={personWebsiteURL}
                      error={(errors.person as any)?.socialLinks?.[0].url}
                    />
                  </InputGroup>
                  <InputGroup label="Personal Social Media URL">
                    {pslFields.map((field: any, index) => {
                      if (field.type === 'Website') {
                        if (pslFields.length === 1)
                          pslAppend({ type: 'Facebook', url: '' }); // Always show at least a social link UI element
                      } else
                        return (
                          <div
                            key={field.id}
                            className="flex items-start mb-2 space-x-3"
                          >
                            <TextSelectField
                              selectProps={{
                                ...register(
                                  `person.socialLinks.${index}.type` as const
                                ),
                                defaultValue: field.type,
                              }}
                              selectOptions={[
                                'Facebook',
                                'Twitter',
                                'Instagram',
                                'LinkedIn',
                              ]}
                              inputProps={{
                                ...register(
                                  `person.socialLinks.${index}.url` as const
                                ),
                                defaultValue: field.url,
                              }}
                              error={
                                (errors.person as any)?.socialLinks?.[index]
                                  ?.url
                              }
                              placeholder={'http://'}
                            />

                            <Button
                              variant="secondary icon"
                              type="button"
                              onClick={() => {
                                if (pslFields.length > 0) {
                                  _.pullAt(pslFields, index);
                                  pslRemove(index);
                                }
                              }}
                              className="ml-4 px-2 mt-1"
                            >
                              <MinusSmIcon className="h-5 w-5 text-gray-400" />
                            </Button>
                          </div>
                        );
                    })}
                    <Button
                      variant="secondary"
                      type="button"
                      disabled={numberOfPersonSocialLinks() >= 3 ? true : false}
                      onClick={() => {
                        // Removing the Website entries from list to get count of SL
                        _.remove(pslFields, (i: any) => i.type === 'Website');
                        if (pslFields.length < 3) {
                          // Can append a max. of 3 SL
                          pslAppend({ type: 'Facebook', url: '' });
                        }
                      }}
                    >
                      <PlusSmIcon className="-ml-2 mr-1 h-5 w-5 text-gray-400" />
                      <span>Add Social Links</span>
                    </Button>
                  </InputGroup>
                </div>
              </div>
              <div className="mt-1 flex items-center space-x-5">
                <InputGroup label="Headshot">
                  <ImageUploadField
                    {...register('person.avatar')}
                    defaultSrc={_.get(
                      data,
                      'contact.person.avatar.filePath',
                      ''
                    )}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        </div>
        {/* Company Information section */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="md:grid md:grid-cols-3 md:gap-6">
            <div className="md:col-span-1">
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Company Information
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                This information will be displayed publicly if added as a
                contact card for a listing.
              </p>
            </div>
            <div className="mt-5 md:mt-0 md:col-span-2">
              {/** <form className="space-y-6" action="#" method="POST">*/}
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-3">
                  <InputGroup label="Company Name">
                    <InputField
                      type="text"
                      {...register(`company.name`)}
                      defaultValue={_.get(data, 'contact.company.name', '')}
                      error={(errors.company as any)?.name}
                    />
                  </InputGroup>

                  <InputGroup label="Title">
                    <InputField
                      type="text"
                      {...register(`company.title`)}
                      defaultValue={_.get(data, 'contact.company.title', '')}
                      error={(errors.company as any)?.title}
                    />
                  </InputGroup>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <InputGroup label="Company Website URL">
                    <PrefixInputField
                      type="text"
                      prefix="http://"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        e.target.value
                          ? setCompanyWebsiteURL(e.target.value)
                          : setCompanyWebsiteURL('');
                        cslAppend({
                          url: companyWebsiteURL,
                          type: 'Website',
                        });
                      }}
                      defaultValue={companyWebsiteURL}
                      error={(errors.company as any)?.socialLinks?.[0].url}
                    />
                  </InputGroup>
                  <InputGroup label="Company Social Media URL">
                    {cslFields.map((field: any, index) => {
                      if (field.type === 'Website') {
                        if (cslFields.length === 1)
                          cslAppend({ type: 'Facebook', url: '' }); // Always show at least a social link UI element
                      } else
                        return (
                          <div
                            key={field.id} // That format removed the error about list requiring unique keys
                            className="flex items-start mb-2 space-x-3"
                          >
                            <TextSelectField
                              selectProps={{
                                ...register(
                                  `company.socialLinks.${index}.type` as const
                                ),
                                defaultValue: field.type,
                              }}
                              selectOptions={[
                                'Facebook',
                                'Twitter',
                                'Instagram',
                                'LinkedIn',
                              ]}
                              inputProps={{
                                ...register(
                                  `company.socialLinks.${index}.url` as const
                                ),
                                defaultValue: field.url,
                              }}
                              error={
                                (errors.company as any)?.socialLinks?.[index]
                                  ?.url
                              }
                              placeholder={'http://'}
                            />

                            <Button
                              variant="secondary icon"
                              type="button"
                              onClick={() => {
                                if (cslFields.length > 0) {
                                  _.pullAt(cslFields, index);
                                  cslRemove(index);
                                }
                              }}
                              className="ml-4 px-2 mt-1"
                            >
                              <MinusSmIcon className="h-5 w-5 text-gray-400" />
                            </Button>
                          </div>
                        );
                    })}
                    <Button
                      variant="secondary"
                      type="button"
                      disabled={
                        numberOfCompanySocialLinks() >= 3 ? true : false
                      }
                      onClick={() => {
                        // Removing the Website entries from list to get count of SL
                        _.remove(cslFields, (i: any) => i.type === 'Website');
                        if (cslFields.length < 3) {
                          // Can append a max. of 3 SL
                          cslAppend({ type: 'Facebook', url: '' });
                        }
                      }}
                    >
                      <PlusSmIcon className="-ml-2 mr-1 h-5 w-5 text-gray-400" />
                      <span>Add Social Links</span>
                    </Button>
                  </InputGroup>
                </div>
              </div>
              <div className="mt-1 flex items-center space-x-5">
                <InputGroup label="Logo">
                  <ImageUploadField
                    {...register('company.logo')}
                    defaultSrc={_.get(
                      data,
                      'contact.company.logo.filePath',
                      ''
                    )}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        </div>
        {/* Footer Action section - buttons*/}
        <div className="flex justify-end">
          {/* TODO: Cancel logic */}
          <CancelFormButton
            hasChanges={isDirty}
            onCleanUp={() => resetFields()}
            onClick={() => history.goBack()}
          />

          <SaveFormButton loading={loading} disabled={!isDirty} />
        </div>
      </div>
    </form>
  );
};

export default ContactDetails;

/**
 * TODO: Create/Move to /Account/Account.graph.ts (Maybe - R&D required for graph queries strategy)
 */

const GET_CONTACT = gql`
  query ContactDetails_ContactData($contactId: String!) {
    contact(contactId: $contactId) {
      id
      person {
        name {
          first
          last
          full
        }
        email
        phones {
          number
          type
        }
        socialLinks {
          url
          type
        }
        avatar {
          filePath
        }
      }
      company {
        name
        title
        socialLinks {
          url
          type
        }
        logo {
          filePath
        }
      }
    }
  }
`;

const UPDATE_CONTACT = gql`
  mutation ContactDetails_UpdateContactData(
    $contactId: String!
    $contact: ContactInput!
  ) {
    contactUpdate(contactId: $contactId, contact: $contact) {
      id
      person {
        name {
          first
          last
          full
        }
        email
        phones {
          number
          type
        }
        socialLinks {
          url
          type
        }
        avatar {
          filePath
        }
      }
      company {
        name
        title
        socialLinks {
          url
          type
        }
        logo {
          filePath
        }
      }
    }
  }
`;

const ADD_CONTACT = gql`
  mutation ContactDetails_AddContactData($contact: ContactInput!) {
    contactAdd(contact: $contact) {
      id
      person {
        name {
          first
          last
          full
        }
        email
        phones {
          number
          type
        }
        socialLinks {
          url
          type
        }
        avatar {
          filePath
        }
      }
      company {
        name
        title
        socialLinks {
          url
          type
        }
        logo {
          filePath
        }
      }
    }
  }
`;

//////
// Abstraction of components created during Profile JSX cleanup
//
/**
 * TODO: Create/Move to /components/InputGroup.tsx and import
 */
const InputGroup = ({
  label,
  children,
  ...props
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <label {...props} className="block text-sm font-medium text-gray-700">
      {label}
    </label>
    {children}
  </div>
);
/**
 * TODO: Create/Move to /components/InputField.tsx and import
 */
const InputField = React.forwardRef<
  HTMLInputElement,
  {
    error?: any;
    [x: string]: any;
  }
>(({ error, ...props }, ref) => (
  <div className="relative">
    <input
      ref={ref}
      {...props}
      className={`${
        error
          ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
          : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
      } appearance-none block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none sm:text-sm`}
    />
    {error ? (
      <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
        <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
      </div>
    ) : null}
    <p className="mt-2 text-sm text-red-600">{error?.message}</p>
  </div>
));
/**
 * TODO: Create/Move to /components/PrefixInputField.tsx and import
 */
const PrefixInputField = React.forwardRef<
  HTMLInputElement,
  {
    prefix: string;
    error?: any;
    [x: string]: any;
  }
>(({ prefix, error, ...props }, ref) => (
  <div className="relative">
    <div className="flex rounded-md shadow-sm">
      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
        {prefix}
      </span>
      <input
        ref={ref}
        {...props}
        type="text"
        id="websiteUrl"
        className="focus:ring-primary-light focus:border-primary-light flex-1 block w-full rounded-none rounded-r-md sm:text-sm border-gray-300"
      />
      {error ? (
        <div className="absolute inset-y-0 -top-7 right-0 pr-3 flex items-center pointer-events-none">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
        </div>
      ) : null}
      <p className="mt-2 text-sm text-red-600">{error?.message}</p>
    </div>
  </div>
));
/**
 * TODO: Create/Move to /components/Button.tsx and import
 */
// MB comment: Based on Mark's button as per Profile.tsx pre- bug/45
// Enhancements: added the "disabled" attribute with disabled logic and associated UI feedbacks
// Currently, only variant=secondary is fully tested, the rest of variants are not tested yet.
const Button = ({
  variant,
  type = 'button',
  disabled = false,
  children,
  className = '',
  onClick,
}: {
  variant: string;
  type: 'button' | 'submit';
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  onClick?: any;
}) => {
  return variant === 'primary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'secondary' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white ' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : variant === 'primary icon' ? (
    <button
      type={type}
      className={`${
        disabled
          ? 'cursor-not-allowed hover:bg-primary'
          : 'hover:bg-primary-dark'
      }  bg-primary border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  ) : variant === 'secondary icon' ? (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${
        disabled ? 'cursor-not-allowed hover:bg-white' : 'hover:bg-gray-50'
      }  bg-white inline-flex items-center px-2 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light ${className}`}
    >
      {children}
    </button>
  ) : null;
};
/**
 * TODO: Create/Move to /components/TextSelectField.tsx and import
 */
const TextSelectField = ({
  selectProps: { ref: sRef, ...selectProps },
  selectOptions,
  inputProps: { ref: iRef, mask, ...inputProps },
  error,
  placeholder,
}: {
  selectProps: { ref: React.ForwardedRef<HTMLSelectElement>; [x: string]: any }; //</HTMLSelectElement>/
  selectOptions: any;
  inputProps: {
    ref: React.ForwardedRef<HTMLInputElement>; //</HTMLInputElement>
    mask?: string;
    [x: string]: any;
  };
  placeholder: any;
  error?: any;
}) => {
  // BUG: Erratic behavor when using Input - i.e. makes other fields content disappear
  // See other comment BUG: below in JSX - To bypass Input
  //const Input = ({ ...props }) =>
  //  mask ? <InputMask mask={mask} {...props} /> : <input {...props} />;

  return (
    <div className="flex flex-col relative">
      <div className="mt-1 flex flex-grow rounded-md shadow-sm">
        <span className="inline-flex items-center rounded-l-md border-none border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
          <select
            ref={sRef}
            {...selectProps}
            className="focus:ring-primary-light focus:border-primary-light border-gray-300 block sm:text-sm bg-gray-50 focus:border-none rounded-md rounded-r-none cursor-pointer"
          >
            {selectOptions.map((option: string, index: number) => (
              <option key={index}>{option}</option>
            ))}
          </select>
        </span>
        {/** BUG: Originally, Input was called here versus input (bug to be fixed)*/}
        <input
          type="text"
          ref={iRef}
          {...inputProps}
          className={`${
            error
              ? `border-red-300 placeholder-red-300 focus:ring-red-500 focus:border-red-500`
              : `border-gray-300 placeholder-gray-400 focus:ring-primary-light focus:border-primary-light`
          } flex-1 block w-full rounded-none rounded-r-md sm:text-sm`}
          placeholder={placeholder}
        />
      </div>
      {error ? (
        <div className="absolute inset-y-0 -top-6 right-0 pr-3 flex items-center pointer-events-none">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
        </div>
      ) : null}
      <p className="mt-2 text-sm text-red-600">{error?.message}</p>
    </div>
  );
};
